import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash, FaTimes, FaArrowLeft, FaGoogle, FaFacebook } from 'react-icons/fa';
import { ShopContext } from '../Context/ShopContext';
import PropTypes from 'prop-types';
import './CSS/loginsignup.css';

const LoginSignup = ({ onClose = () => {} }) => {
    const navigate = useNavigate();
    const { mergeGuestCart, showToast, guestCart } = useContext(ShopContext);

    const initialFormData = {
        name: '',
        email: '',
        phone: '',
        password: '',
        reEnterPassword: '',
        agreeToTerms: false,
    };

    const [formData, setFormData] = useState(initialFormData);
    const [showPassword, setShowPassword] = useState(false);
    const [currentForm, setCurrentForm] = useState('login');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const switchForm = (form) => {
        setCurrentForm(form);
        setErrors({});
        setFormData(initialFormData);
    };

    const validateForm = () => {
        const newErrors = {};

        if (currentForm === 'signup') {
            if (!formData.name.trim()) newErrors.name = 'Name is required';
            if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
            else if (!/^(?:254\d{9}|0\d{9})$/.test(formData.phone)) newErrors.phone = 'Enter a valid Kenyan number (e.g., 0712345678)';

            if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
        }

        if (currentForm !== 'login' || (currentForm === 'login' && formData.email)) {
            if (!formData.email) newErrors.email = 'Email is required';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
        }

        if (currentForm !== 'forgotPassword') {
            if (!formData.password) newErrors.password = 'Password is required';
            else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        }

        if (currentForm === 'signup' && formData.password !== formData.reEnterPassword) {
            newErrors.reEnterPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSocialLogin = (provider) => {
        showToast(`Redirecting to ${provider} login...`, 'info');
        // In a real app, you would implement actual social login here
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!formData.email) {
            setErrors({ email: 'Email is required to reset password' });
            return;
        }
        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToast('If an account exists for this email, a reset link will be sent.', 'success');
            switchForm('login');
        } catch (error) {
            showToast('An error occurred. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const endpoint = currentForm === 'login' ? 'login' : 'signup';
            const response = await fetch(`https://backend-qi5u.onrender.com/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(currentForm === 'login' ? {
                    email: formData.email,
                    password: formData.password
                } : {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password
                }),
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Request failed');

            localStorage.setItem('auth-token', data.token);
            showToast(currentForm === 'login' ? 'Login successful!' : 'Account created!', 'success');

            if (guestCart && Object.keys(guestCart).length > 0) {
                await mergeGuestCart(guestCart);
            }

            navigate('/');
            onClose();
        } catch (error) {
            showToast(error.message || 'An error occurred. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const renderFormContent = () => {
        if (currentForm === 'forgotPassword') {
            return (
                <>
                    <h1>Reset Password</h1>
                    <p className="form-description">Enter your email to receive a password reset link.</p>
                    <form onSubmit={handlePasswordReset} noValidate>
                        <div className="loginsignup-fields">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email Address"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className={errors.email ? 'error' : ''}
                            />
                            {errors.email && <span className="error-message">{errors.email}</span>}
                        </div>
                        <button type="submit" className="loginsignup-submit" disabled={isLoading}>
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                    </form>
                    <p className="loginsignup-login">
                        <button type="button" className="form-toggle back-to-login" onClick={() => switchForm('login')}>
                            <FaArrowLeft /> Back to Login
                        </button>
                    </p>
                </>
            );
        }

        return (
            <>
                <h1>{currentForm === 'login' ? 'Welcome Back' : 'Create Account'}</h1>
                <p className="form-description">
                    {currentForm === 'login' ? 'Log in to access your account' : 'Join us today and start shopping'}
                </p>

                <div className="social-login-buttons">
                    <button 
                        className="social-button google" 
                        onClick={() => handleSocialLogin('Google')}
                    >
                        <FaGoogle /> Continue with Google
                    </button>
                    <button 
                        className="social-button facebook" 
                        onClick={() => handleSocialLogin('Facebook')}
                    >
                        <FaFacebook /> Continue with Facebook
                    </button>
                </div>

                <div className="divider">
                    <span>or</span>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    {currentForm === 'signup' && (
                        <>
                            <div className="loginsignup-fields">
                                <input 
                                    type="text" 
                                    name="name" 
                                    placeholder="Your Name" 
                                    value={formData.name} 
                                    onChange={handleChange} 
                                    className={errors.name ? 'error' : ''} 
                                />
                                {errors.name && <span className="error-message">{errors.name}</span>}
                            </div>
                            <div className="loginsignup-fields">
                                <input 
                                    type="tel" 
                                    name="phone" 
                                    placeholder="Phone Number (e.g., 0712345678)" 
                                    value={formData.phone} 
                                    onChange={handleChange} 
                                    className={errors.phone ? 'error' : ''}
                                />
                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                            </div>
                        </>
                    )}
                    <div className="loginsignup-fields">
                        <input 
                            type="email" 
                            name="email" 
                            placeholder="Email Address" 
                            value={formData.email} 
                            onChange={handleChange} 
                            className={errors.email ? 'error' : ''}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>
                    <div className="loginsignup-fields password-field">
                        <input 
                            type={showPassword ? "text" : "password"} 
                            name="password" 
                            placeholder="Password" 
                            value={formData.password} 
                            onChange={handleChange} 
                            className={errors.password ? 'error' : ''} 
                        />
                        <button 
                            type="button" 
                            className="password-toggle" 
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>
                    {currentForm === 'signup' && (
                        <>
                            <div className="loginsignup-fields password-field">
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    name="reEnterPassword" 
                                    placeholder="Re-enter Password" 
                                    value={formData.reEnterPassword} 
                                    onChange={handleChange} 
                                    className={errors.reEnterPassword ? 'error' : ''}
                                />
                                {errors.reEnterPassword && <span className="error-message">{errors.reEnterPassword}</span>}
                            </div>
                            <div className="loginsignup-agree">
                                <input 
                                    type="checkbox" 
                                    name="agreeToTerms" 
                                    id="agreeToTerms" 
                                    checked={formData.agreeToTerms} 
                                    onChange={handleChange}
                                />
                                <label htmlFor="agreeToTerms">
                                    I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">terms and conditions</a>
                                </label>
                                {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
                            </div>
                        </>
                    )}
                    <button type="submit" className="loginsignup-submit" disabled={isLoading}>
                        {isLoading ? 'Processing...' : (currentForm === 'login' ? 'Login' : 'Sign Up')}
                    </button>
                </form>

                {currentForm === 'login' && (
                    <div className="forgot-password-link">
                        <button type="button" onClick={() => switchForm('forgotPassword')}>
                            Forgot Password?
                        </button>
                    </div>
                )}

                <p className="loginsignup-login">
                    {currentForm === 'login' ? "Don't have an account? " : "Already have an account? "}
                    <button 
                        type="button" 
                        className="form-toggle" 
                        onClick={() => switchForm(currentForm === 'login' ? 'signup' : 'login')}
                    >
                        {currentForm === 'login' ? 'Sign Up' : 'Login'}
                    </button>
                </p>
            </>
        );
    };

    return (
        <div className="loginsignup" onClick={onClose}>
            <div className="loginsignup-container" onClick={(e) => e.stopPropagation()}>
                <button className="loginsignup-close" onClick={onClose} aria-label="Close">
                    <FaTimes />
                </button>
                {renderFormContent()}
                <ToastContainer position="bottom-right" autoClose={3000} />
            </div>
        </div>
    );
};

LoginSignup.propTypes = {
    onClose: PropTypes.func,
};

export default LoginSignup;